import React, { useEffect, useRef } from "react";
import { elakayAutoText } from "./dataElakay";
import "./InfiniteScrollLoader.css";

const InfiniteScrollLoader = ({
  enabled,
  hasMore,
  loading,
  error,
  onLoadMore,
  onRetry,
  skeleton = null,
  rootMargin = "600px 0px",
  label,
}) => {
  const sentinelRef = useRef(null);
  const requestRef = useRef(onLoadMore);
  requestRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled || !hasMore || loading || error || !sentinelRef.current) return undefined;
    const node = sentinelRef.current;
    if (typeof IntersectionObserver === "undefined") return undefined;
    // The page scrolls inside .elakayPage, not the window: with root=null the
    // rootMargin (preload distance) was clipped by that scroller, so the
    // next page only loaded once the sentinel was actually on screen.
    const root = node.closest?.(".elakayPage") || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) requestRef.current?.();
    }, { root, rootMargin, threshold: 0.01 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, hasMore, loading, error, rootMargin]);

  if (!enabled) return null;
  return (
    <div className="elk-infinite-loader" aria-live="polite" aria-busy={loading ? "true" : "false"}>
      {loading ? skeleton : null}
      {error ? (
        <div className="elk-infinite-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={onRetry || onLoadMore}>
            {elakayAutoText("h_infinite_retry", "Retry")}
          </button>
        </div>
      ) : null}
      {hasMore && !error ? (
        <div
          ref={sentinelRef}
          className="elk-infinite-sentinel"
          role="status"
          aria-label={label || elakayAutoText("h_infinite_loading", "Load more results")}
          tabIndex={0}
          onFocus={() => { if (!loading) requestRef.current?.(); }}
        />
      ) : null}
    </div>
  );
};

export default InfiniteScrollLoader;

